import { CaseTag } from '@debbie/common/dist/cases/index.js';
import { CaseStatus } from '@debbie/common/dist/cases/index.js';
import { Entity } from '@debbie/common/dist/index.js';
import { PropertyDataType } from '@debbie/common/dist/properties/index.js';
import { eq, fromTuple, gt, isIn, isNull, lt, or, toTuple, } from '@debbie/common/dist/query/index.js';
import { objectBy, search, sortBy, uniqueBy, } from '@debbie/common/dist/util/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import { useUpdateTypes } from '@debbie/cortex/dist/update-types/index.js';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '../../Button.js';
import Checkbox from '../../Checkbox.js';
import DateInput from '../../DateInput.js';
import Errors from '../../Errors.js';
import Input from '../../Input.js';
import Loading from '../../Loading.js';
import Select from '../../Select.js';
import Textarea from '../../Textarea.js';
import { DropDownMenuItem } from '../DropDownMenuItem.js';
import { findUpdateTypeFilter } from '../util.js';
// Standard
export const AssociatedCasePicker = (props) => {
    const lang = useSelector((s) => s.lang);
    const updateFilter = (associated) => {
        const filter = isNull('cases.associationId');
        props.setFilter({
            ...(associated ? { type: 'not', filter } : filter),
            filterKey: 'isAssociatedCase',
            id: props.filter?.id ?? crypto.randomUUID(),
        });
        props.onCloseMenu();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(DropDownMenuItem, { label: lang.views.associated, icon: props.filter?.type === 'not' ? 'bool' : undefined, onClick: () => updateFilter(true) }),
        React.createElement(DropDownMenuItem, { label: lang.views.notAssociated, icon: props.filter?.type === 'null' ? 'bool' : undefined, onClick: () => updateFilter(false) })));
};
export const EnumPicker = (key, property, options) => (props) => {
    const lang = useSelector((s) => s.lang);
    const filter = props.filter;
    // Handle not filter that wraps an in filter
    const isNotFilter = filter?.type === 'not' && filter?.filter?.type === 'in';
    const inFilter = isNotFilter ? filter.filter : filter;
    if (inFilter != null && inFilter.type !== 'in') {
        throw new Error('EnumFilter should result in an in filter or a not filter wrapping an in filter');
    }
    if (inFilter != null && fromTuple(inFilter.property) !== property) {
        throw new Error('Filter matches the wrong property');
    }
    const updateFilter = (value) => {
        if (filter == null || inFilter == null) {
            const newFilter = {
                filterKey: key,
                id: crypto.randomUUID(),
                type: 'in',
                property: toTuple(property),
                values: [value],
            };
            props.setFilter(newFilter);
            return;
        }
        const index = inFilter.values.indexOf(value);
        if (index != -1) {
            // Exists
            const updatedValues = [
                ...inFilter.values.slice(0, index),
                ...inFilter.values.slice(index + 1),
            ];
            if (isNotFilter) {
                // @ts-expect-error
                props.setFilter({
                    id: filter.id,
                    filterKey: filter.filterKey,
                    filter: {
                        ...inFilter,
                        values: updatedValues,
                    },
                });
            }
            else {
                props.setFilter({
                    ...filter,
                    // @ts-expect-error
                    values: updatedValues,
                });
            }
        }
        else {
            const updatedValues = [...inFilter.values, value];
            if (isNotFilter) {
                props.setFilter({
                    ...filter,
                    filter: {
                        ...inFilter,
                        values: updatedValues,
                    },
                });
            }
            else {
                // @ts-expect-error
                props.setFilter({ ...filter, values: updatedValues });
            }
        }
    };
    return (React.createElement(React.Fragment, null, search(options, (o) => o.label(lang), props.searchText, {}).map((enumOption) => {
        return (React.createElement(DropDownMenuItem, { key: `${enumOption.value}`, label: enumOption.label(lang), icon: enumOption.icon, onClick: () => {
                updateFilter(enumOption.value);
                props.onCloseMenu();
            }, checkbox: {
                checked: inFilter
                    ? inFilter.values.includes(enumOption.value)
                    : false,
                onChange: (e) => {
                    updateFilter(enumOption.value);
                },
            } }));
    })));
};
export const DatePicker = (key, property) => (props) => {
    const filter = props.filter;
    if (filter != null &&
        filter.type != 'lt' &&
        filter.type != 'gt' &&
        filter.type != 'eq') {
        throw new Error('Invalid filter');
    }
    const updateFilter = (value) => {
        if (filter == null) {
            const newFilter = {
                ...gt(property, value),
                filterKey: key,
                id: crypto.randomUUID(),
            };
            props.setFilter(newFilter);
            props.onCloseMenu();
            return;
        }
        props.setFilter({
            ...filter,
            value: value,
        });
        props.onCloseMenu();
    };
    const options = [
        {
            label: '1 dag siden',
            filterDate: () => moment().subtract(1, 'day').toISOString(),
        },
        {
            label: '3 dage siden',
            filterDate: () => moment().subtract(3, 'days').toISOString(),
        },
        {
            label: '1 uge siden',
            filterDate: () => moment().subtract(1, 'week').toISOString(),
        },
        {
            label: '2 uger siden',
            filterDate: () => moment().subtract(2, 'weeks').toISOString(),
        },
        {
            label: '1 måned siden',
            filterDate: () => moment().subtract(1, 'month').toISOString(),
        },
    ];
    return (React.createElement(React.Fragment, null,
        (props.searchText != ''
            ? search(options, (o) => o.label, props.searchText, {})
            : options).map((option) => {
            return (React.createElement(DropDownMenuItem, { key: option.label, label: option.label, onClick: () => 
                // @ts-expect-error
                updateFilter(option.filterDate()) }));
        }),
        React.createElement(DateInput, { style: { margin: 0 }, allowNull: true, placeholder: "tidspunkt", value: filter && typeof filter.value === 'string'
                ? moment(filter.value)
                : undefined, 
            // @ts-expect-error
            onChange: (date) => updateFilter(date?.toISOString()) })));
};
export const ListPicker = (filterKey, propertyName, dataLoader, getKey, getLabel, getIcon) => (props) => {
    const filter = props.filter;
    const lang = useSelector((s) => s.lang);
    const loader = dataLoader();
    // Handle not filter that wraps an in filter
    const isNotFilter = filter?.type === 'not' && filter?.filter?.type === 'in';
    const inFilter = isNotFilter ? filter.filter : filter;
    if (inFilter != null && inFilter.type != 'in') {
        throw new Error('Invalid filter - expected in filter or not filter wrapping an in filter');
    }
    const [selected, setSelected] = useState(inFilter?.values ?? []);
    const updateFilter = (value) => {
        if (filter == null || inFilter == null) {
            const newFilter = {
                // @ts-expect-error
                ...isIn(propertyName, [value]),
                filterKey: filterKey,
                id: crypto.randomUUID(),
            };
            props.setFilter(newFilter);
            return;
        }
        const index = inFilter.values.indexOf(value);
        if (index != -1) {
            // Exists
            const updatedValues = [
                ...inFilter.values.slice(0, index),
                ...inFilter.values.slice(index + 1),
            ];
            if (isNotFilter) {
                props.setFilter({
                    ...filter,
                    filter: {
                        ...inFilter,
                        values: updatedValues,
                    },
                });
            }
            else {
                props.setFilter({
                    ...filter,
                    // @ts-expect-error
                    values: updatedValues,
                });
            }
        }
        else {
            const updatedValues = [...inFilter.values, value];
            if (isNotFilter) {
                props.setFilter({
                    ...filter,
                    filter: {
                        ...inFilter,
                        values: updatedValues,
                    },
                });
            }
            else {
                // @ts-expect-error
                props.setFilter({ ...filter, values: updatedValues });
            }
        }
    };
    if (h.isLoading(loader)) {
        return React.createElement(Loading, { width: "20px" });
    }
    if (h.isError(loader)) {
        if (loader.error instanceof Error) {
            return React.createElement(Errors, { errors: [loader.error.message] });
        }
        return React.createElement(Errors, { errors: [lang.misc.errorOccured] });
    }
    return (React.createElement(React.Fragment, null, h.hasData(loader)
        ? (props.searchText === ''
            ? filter
                ? sortBy(loader.data, (item) => selected.includes(getKey(item)) ? '0' : '1')
                : loader.data
            : search(loader.data, (item) => getLabel(item, lang), props.searchText, {})).map((item) => {
            return (React.createElement(DropDownMenuItem, { key: getKey(item), label: getLabel(item, lang), icon: getIcon ? getIcon(item) : undefined, onClick: () => {
                    updateFilter(getKey(item));
                    props.onCloseMenu();
                }, checkbox: {
                    checked: inFilter
                        ? inFilter.values.includes(getKey(item))
                        : false,
                    onChange: (e) => {
                        updateFilter(getKey(item));
                    },
                } }));
        })
        : null));
};
export const BooleanPicker = (filterKey, propertyName, options) => (props) => {
    const lang = useSelector((s) => s.lang);
    const filter = props.filter;
    if (filter != null && filter.type !== 'eq') {
        throw new Error('BooleanFilter should result in an eq filter');
    }
    if (filter != null && fromTuple(filter.property) !== propertyName) {
        throw new Error('Filter matches the wrong property');
    }
    const updateFilter = (value) => {
        if (filter == null) {
            const newFilter = {
                filterKey: filterKey,
                id: crypto.randomUUID(),
                type: 'eq',
                property: toTuple(propertyName),
                value: value,
            };
            props.setFilter(newFilter);
            return;
        }
        // Update existing filter
        props.setFilter({
            ...filter,
            value: value,
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(DropDownMenuItem, { label: options?.yesName ? options.yesName(lang) : lang.misc.yes, onClick: () => {
                updateFilter(true);
                props.onCloseMenu();
            } }),
        React.createElement(DropDownMenuItem, { label: options?.noName ? options.noName(lang) : lang.misc.no, onClick: () => {
                updateFilter(false);
                props.onCloseMenu();
            } })));
};
export const InputListPicker = (filterKey, propertyName, type) => (props) => {
    const filter = props.filter;
    const lang = useSelector((s) => s.lang);
    // Handle not filter that wraps an in filter
    const isNotFilter = filter?.type === 'not' && filter?.filter?.type === 'in';
    const inFilter = isNotFilter ? filter.filter : filter;
    if (inFilter != null && inFilter.type != 'in') {
        throw new Error('Invalid filter - expected in filter or not filter wrapping an in filter');
    }
    const [text, setText] = useState((inFilter?.values ?? []).join(', '));
    const updateFilter = (values) => {
        if (filter == null || inFilter == null) {
            const newFilter = {
                // @ts-expect-error
                ...isIn(propertyName, values),
                filterKey: filterKey,
                id: crypto.randomUUID(),
            };
            props.setFilter(newFilter);
            return;
        }
        if (isNotFilter) {
            props.setFilter({
                ...filter,
                filter: {
                    ...inFilter,
                    values: values,
                },
            });
        }
        else {
            props.setFilter({
                ...filter,
                // @ts-expect-error
                values: values,
            });
        }
    };
    const update = (value) => {
        const ids = value
            .split(/[\s,;]/)
            .filter((x) => x != '')
            .map((x) => x.trim());
        console.log(ids);
        updateFilter(ids);
        props.onCloseMenu();
    };
    const parsedIds = text
        .split(/[\s,;]/)
        .filter((x) => x != '')
        .map((x) => x.trim());
    const nonNumberId = parsedIds.find((id) => {
        if (type === 'float') {
            if (Number.isNaN(parseFloat(id))) {
                return true;
            }
        }
        else if (type === 'int') {
            if (Number.isNaN(parseInt(id))) {
                return true;
            }
        }
        return false;
    });
    const parseError = type == 'int'
        ? nonNumberId != null
            ? `${nonNumberId} ${lang.views.invalidNumberError}`
            : null
        : null;
    console.log(nonNumberId, parseError);
    return (React.createElement(React.Fragment, null,
        React.createElement(Textarea, { autoFocus: true, style: { padding: '8px', marginBottom: '8px' }, value: text, onChange: (e) => {
                setText(e.currentTarget.value);
            }, onKeyDown: (e) => {
                if (e.key === 'Enter' && e.getModifierState('Shift')) {
                    update(e.currentTarget.value);
                }
            } }),
        parseError != null ? React.createElement(Errors, { errors: [parseError] }) : null,
        React.createElement(Button, { disabled: parseError != null, onClick: () => {
                update(text);
            } }, lang.misc.apply)));
};
// Custom
export const PausedUpdatesPicker = (props) => {
    const lang = useSelector((s) => s.lang);
    const updateTypesLoader = useUpdateTypes();
    const originalFilter = props.filter;
    // Handle not filter that wraps an 'and' filter
    const isNotFilter = originalFilter?.type === 'not' && originalFilter?.filter?.type === 'and';
    const andFilter = isNotFilter
        ? originalFilter.filter
        : originalFilter?.type === 'and'
            ? originalFilter
            : null; // This is the 'and' filter to operate on, or null if no filter yet
    if (andFilter != null && andFilter.type !== 'and') {
        throw new Error('PausedUpdatesPicker should result in an and filter or a not filter wrapping an and filter');
    }
    const selectedFilter = findUpdateTypeFilter(andFilter?.filters);
    const selected = selectedFilter && selectedFilter.type === 'in' ? selectedFilter.values : [];
    const [initialSelected] = useState(selected);
    const updateFilter = (updateTypeId) => {
        const currentId = originalFilter?.id ?? crypto.randomUUID();
        const currentFilterKey = originalFilter?.filterKey ?? 'pausedWithUpdates';
        let values = selected ? [...selected] : []; // Make a copy
        const idx = values.indexOf(updateTypeId);
        if (idx != -1) {
            values = [...values.slice(0, idx), ...values.slice(idx + 1)];
        }
        else {
            values = [...selected, updateTypeId];
        }
        if (values.length === 0) {
            props.setFilter({ id: currentId, remove: true }); // Remove by top-level ID
            return;
        }
        const andFilterPayload = {
            // Core 'and' filter structure
            type: 'and',
            filters: [
                isIn('updates.updateTypeId', values),
                or(isNull('updates.continuedAt'), gt('updates.continuedAt', { fn: 'now' })),
                lt('updates.pausedAt', { fn: 'now' }),
                eq('cases.status', CaseStatus.VERIFIED),
            ],
        };
        if (isNotFilter && originalFilter) {
            props.setFilter({
                ...originalFilter, // Spread the 'not' filter (which is an AugmentedFilter)
                filter: andFilterPayload, // Update its inner 'and' filter
            });
        }
        else {
            // Direct 'and' filter or new filter
            const newAugmentedAndFilter = {
                ...andFilterPayload,
                id: currentId,
                filterKey: currentFilterKey, // Ensure type compatibility
            };
            props.setFilter(newAugmentedAndFilter);
        }
    };
    if (h.isError(updateTypesLoader)) {
        if (updateTypesLoader.error instanceof Error) {
            return React.createElement(Errors, { errors: [updateTypesLoader.error.message] });
        }
        return React.createElement(Errors, { errors: [lang.misc.errorOccured] });
    }
    return (React.createElement(React.Fragment, null,
        h.isLoading(updateTypesLoader) ? React.createElement(Loading, { width: "20px" }) : null,
        h.hasData(updateTypesLoader)
            ? (props.searchText === ''
                ? sortBy(updateTypesLoader.data, (ut) => initialSelected.includes(ut.updateTypeId) ? '0' : '1')
                : search(updateTypesLoader.data, (t) => lang.updateTypes[t.updateTypeId] ?? t.label, props.searchText, {})).map((updateType) => {
                return (React.createElement(DropDownMenuItem, { key: updateType.updateTypeId, label: lang.updateTypes[updateType.updateTypeId] ?? updateType.label, onClick: () => {
                        updateFilter(updateType.updateTypeId);
                        props.onCloseMenu();
                    }, checkbox: {
                        checked: selected.includes(updateType.updateTypeId),
                        onChange: () => {
                            updateFilter(updateType.updateTypeId);
                        },
                    } }));
            })
            : null));
};
export const HistoricallyPausedUpdatesPicker = (props) => {
    const lang = useSelector((s) => s.lang);
    const updateTypesLoader = useUpdateTypes();
    const originalFilter = props.filter;
    const isNotFilter = originalFilter?.type === 'not' && originalFilter?.filter?.type === 'and';
    const andFilter = isNotFilter
        ? originalFilter.filter
        : originalFilter?.type === 'and'
            ? originalFilter
            : null;
    if (andFilter != null && andFilter.type !== 'and') {
        throw new Error('HistoricallyPausedUpdatesPicker should result in an and filter or a not filter wrapping an and filter');
    }
    const selectedFilter = findUpdateTypeFilter(andFilter?.filters);
    const selected = selectedFilter && selectedFilter.type === 'in' ? selectedFilter.values : [];
    const [initialSelected] = useState(selected);
    const updateFilter = (updateTypeId) => {
        const currentId = originalFilter?.id ?? crypto.randomUUID();
        const currentFilterKey = originalFilter?.filterKey ?? 'historicallyPausedUpdates';
        let values = selected ? [...selected] : [];
        const idx = values.indexOf(updateTypeId);
        if (idx != -1) {
            values = [...values.slice(0, idx), ...values.slice(idx + 1)];
        }
        else {
            values = [...values, updateTypeId];
        }
        if (values.length === 0) {
            props.setFilter({ id: currentId, remove: true });
            return;
        }
        const andFilterPayload = {
            type: 'and',
            filters: [
                isIn('updates.updateTypeId', values),
                lt('updates.pausedAt', { fn: 'now' }),
            ],
        };
        if (isNotFilter && originalFilter) {
            props.setFilter({
                ...originalFilter,
                filter: andFilterPayload,
            });
        }
        else {
            const newAugmentedAndFilter = {
                ...andFilterPayload,
                id: currentId,
                filterKey: currentFilterKey,
            };
            props.setFilter(newAugmentedAndFilter);
        }
    };
    if (h.isError(updateTypesLoader)) {
        if (updateTypesLoader.error instanceof Error) {
            return React.createElement(Errors, { errors: [updateTypesLoader.error.message] });
        }
        return React.createElement(Errors, { errors: [lang.misc.errorOccured] });
    }
    return (React.createElement(React.Fragment, null,
        h.isLoading(updateTypesLoader) ? React.createElement(Loading, { width: "20px" }) : null,
        h.hasData(updateTypesLoader)
            ? (props.searchText === ''
                ? sortBy(updateTypesLoader.data, (ut) => initialSelected.includes(ut.updateTypeId) ? '0' : '1')
                : search(updateTypesLoader.data, (t) => lang.updateTypes[t.updateTypeId] ?? t.label, props.searchText, {})).map((updateType) => {
                return (React.createElement(DropDownMenuItem, { key: updateType.updateTypeId, label: lang.updateTypes[updateType.updateTypeId] ?? updateType.label, onClick: () => {
                        updateFilter(updateType.updateTypeId);
                        props.onCloseMenu();
                    }, checkbox: {
                        checked: selected.includes(updateType.updateTypeId),
                        onChange: () => {
                            updateFilter(updateType.updateTypeId);
                        },
                    } }));
            })
            : null));
};
export const PropertyPicker = (props) => {
    const filterKey = 'property';
    const lang = useSelector((s) => s.lang);
    const typesLoader = usePropertyTypes();
    const typeLookup = h.hasData(typesLoader)
        ? objectBy(typesLoader.data, (t) => t.propertyTypeId)
        : {};
    const filter = props.filter;
    if (filter != null && filter.type != 'and') {
        throw new Error('Invalid filter');
    }
    const handleFilter = filter?.filters.find((f) => f.type === 'eq' &&
        f.property[0] === 'properties' &&
        f.property[1] === 'handle');
    const dataFilter = filter?.filters.find((f) => (f.type === 'eq' || f.type === 'in') &&
        f.property[0] === 'properties' &&
        f.property[1] === 'data');
    const [propertyTypeId, setPropertyTypeId] = useState(handleFilter && handleFilter.type == 'eq' && h.hasData(typesLoader)
        ? (typesLoader.data.find((t) => t.handle === handleFilter.value)
            ?.propertyTypeId ?? '')
        : '');
    const [value, setValue] = useState(dataFilter
        ? dataFilter.type === 'eq'
            ? // @ts-expect-error
                JSON.parse(dataFilter.value)
            : dataFilter.type === 'in'
                ? // @ts-expect-error
                    dataFilter.values.map((x) => JSON.parse(x)).join(', ')
                : ''
        : '');
    const [commaSeparated, setCommaSeparated] = useState(dataFilter && dataFilter.type === 'in');
    // TODO: Possibly add a useEffect here in case property types haven't loaded (they are loaded for the display though, so unlikely)
    const updateFilter = (data) => {
        const id = filter?.id ?? crypto.randomUUID();
        const typeId = data.propertyTypeId ?? propertyTypeId;
        const val = data.value ?? value;
        const propertyType = typeLookup[typeId];
        if (propertyType == null) {
            throw new Error('Property type not found');
        }
        const path = propertyType.ref == Entity.Case ? ['cases'] : ['caseVouchers'];
        const f = {
            id,
            filterKey,
            type: 'and',
            filters: [
                eq('properties.handle', propertyType.handle, path),
                eq('properties.current', true, path),
                commaSeparated
                    ? isIn('properties.data', 
                    //@ts-expect-error
                    val.split(',').map((v) => JSON.stringify(v.trim())), path)
                    : //@ts-expect-error
                        eq('properties.data', JSON.stringify(val), path),
            ],
        };
        props.setFilter(f);
    };
    if (h.isError(typesLoader)) {
        if (typesLoader.error instanceof Error) {
            return React.createElement(Errors, { errors: [typesLoader.error.message] });
        }
        return React.createElement(Errors, { errors: [lang.misc.errorOccured] });
    }
    const canSubmit = propertyTypeId != '';
    if (h.hasData(typesLoader)) {
        console.log(propertyTypeId);
        console.log(typesLoader.data.find((t) => t.handle === propertyTypeId));
    }
    // TODO: Permission level
    return (React.createElement(React.Fragment, null,
        h.isLoading(typesLoader) ? React.createElement(Loading, { width: "1em" }) : null,
        h.hasData(typesLoader) ? (React.createElement(React.Fragment, null,
            React.createElement(Select, { value: propertyTypeId, onChange: (e) => {
                    setPropertyTypeId(e.currentTarget.value);
                } },
                React.createElement("option", { value: "" }),
                sortBy(uniqueBy(typesLoader.data, (t) => `${t.handle}-${t.type}-${t.ref}`), (t) => t.label)
                    .filter((t) => t.type === PropertyDataType.singleLineText)
                    .map((type) => {
                    return (React.createElement("option", { key: type.propertyTypeId, value: type.propertyTypeId },
                        type.label,
                        " (",
                        `${type.handle}`,
                        ")"));
                })),
            typesLoader.data.find((t) => t.propertyTypeId === propertyTypeId)
                ?.type === PropertyDataType.singleLineText ? (React.createElement(Input, { placeholder: lang.misc.value, value: value, onChange: (e) => {
                    setValue(e.currentTarget.value);
                } })) : null,
            typesLoader.data.find((t) => t.propertyTypeId === propertyTypeId) ? (React.createElement(Checkbox, { checked: commaSeparated, onChange: (e) => {
                    setCommaSeparated(e.currentTarget.checked);
                }, placeholder: lang.views.commaSeparated })) : null,
            React.createElement(Button, { disabled: !canSubmit, onClick: () => {
                    updateFilter({});
                    props.onCloseMenu();
                } }, lang.misc.apply))) : null));
};
export const CaseTagPicker = (props) => {
    const lang = useSelector((s) => s.lang);
    const filter = props.filter;
    // Handle not filter that wraps an in filter
    const isNotFilter = filter?.type === 'not' && filter?.filter?.type === 'in';
    const inFilter = isNotFilter ? filter.filter : filter;
    if (inFilter != null && inFilter.type !== 'in') {
        throw new Error('CaseTagPicker should result in an in filter or a not filter wrapping an in filter');
    }
    const options = Object.values(CaseTag).map((ct) => ({
        value: ct,
        label: (lang) => lang.caseTags[ct],
    }));
    const updateFilter = (value) => {
        if (filter == null || inFilter == null) {
            const rawFilter = {
                type: 'in',
                property: toTuple('cases.tags'),
                values: [value],
                jsonKey: 'tag',
            };
            const newFilter = {
                filterKey: 'caseTags',
                id: crypto.randomUUID(),
                ...rawFilter,
            };
            props.setFilter(newFilter);
            return;
        }
        const index = inFilter.values.indexOf(value);
        if (index != -1) {
            // Exists - remove it
            const updatedValues = [
                ...inFilter.values.slice(0, index),
                ...inFilter.values.slice(index + 1),
            ];
            if (isNotFilter) {
                // @ts-expect-error
                props.setFilter({
                    id: filter.id,
                    filterKey: filter.filterKey,
                    filter: {
                        ...inFilter,
                        values: updatedValues,
                    },
                });
            }
            else {
                props.setFilter({
                    ...filter,
                    // @ts-expect-error
                    values: updatedValues,
                });
            }
        }
        else {
            const updatedValues = [...inFilter.values, value];
            if (isNotFilter) {
                props.setFilter({
                    ...filter,
                    filter: {
                        ...inFilter,
                        values: updatedValues,
                    },
                });
            }
            else {
                // @ts-expect-error
                props.setFilter({ ...filter, values: updatedValues });
            }
        }
    };
    return (React.createElement(React.Fragment, null, search(options, (o) => o.label(lang), props.searchText, {}).map((option) => {
        return (React.createElement(DropDownMenuItem, { key: option.value, label: option.label(lang), onClick: () => {
                updateFilter(option.value);
                props.onCloseMenu();
            }, checkbox: {
                checked: inFilter
                    ? inFilter.values.includes(option.value)
                    : false,
                onChange: () => {
                    updateFilter(option.value);
                },
            } }));
    })));
};
//# sourceMappingURL=FilterPickers.js.map